import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  Flex,
  Badge,
  useColorModeValue,
  Select,
  Button,
} from '@chakra-ui/react';
import { MdVisibility, MdDelete, MdDownload } from 'react-icons/md';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import Pagination from 'components/pagination';
import BackButton from 'components/BackButton';

const columnHelper = createColumnHelper();

const logs = [
  {
    id: 1,
    userName: 'Alice Johnson',
    logType: 'Info',
    logDescription: 'User logged in successfully.',
    logDate: '2025-02-10T14:23:00Z',
  },
  {
    id: 2,
    userName: 'Bob Williams',
    logType: 'Error',
    logDescription: 'Failed login attempt due to incorrect password.',
    logDate: '2025-02-10T15:45:00Z',
  },
  {
    id: 3,
    userName: 'Charlie Davis',
    logType: 'Warning',
    logDescription: 'User attempted to access restricted resource.',
    logDate: '2025-02-11T08:10:00Z',
  },
  {
    id: 4,
    userName: 'Diana Roberts',
    logType: 'Info',
    logDescription: 'Profile updated successfully.',
    logDate: '2025-02-11T09:30:00Z',
  },
  {
    id: 5,
    userName: 'Ethan Brown',
    logType: 'Error',
    logDescription: 'Database connection timeout error.',
    logDate: '2025-02-12T11:15:00Z',
  },
  {
    id: 6,
    userName: 'Fiona Clarke',
    logType: 'Info',
    logDescription: 'User logged out.',
    logDate: '2025-02-12T13:00:00Z',
  },
  {
    id: 7,
    userName: 'George Miller',
    logType: 'Warning',
    logDescription: 'High number of failed login attempts detected.',
    logDate: '2025-02-13T07:50:00Z',
  },
  {
    id: 8,
    userName: 'Hannah Scott',
    logType: 'Info',
    logDescription: 'New user registered.',
    logDate: '2025-02-13T10:05:00Z',
  },
  {
    id: 9,
    userName: 'Ian Carter',
    logType: 'Error',
    logDescription: 'Unauthorized API access attempt.',
    logDate: '2025-02-13T12:45:00Z',
  },
  {
    id: 10,
    userName: 'Julia White',
    logType: 'Info',
    logDescription: 'User changed password.',
    logDate: '2025-02-14T09:20:00Z',
  },
];

const TeamLogs = () => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const totalPages = Math.ceil(logs.length / itemsPerPage);
  const paginatedLogs = logs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const columns = [
    columnHelper.accessor(
      (row, index) => (currentPage - 1) * itemsPerPage + index + 1,
      {
        id: '#',
        header: () => (
          <Text fontSize="12px" color="gray.400" fontWeight="bold">
            #
          </Text>
        ),
        cell: (info) => (
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        ),
      },
    ),
    columnHelper.accessor('userName', {
      id: 'userName',
      header: () => (
        <Text color="gray.400" fontWeight="bold">
          User Name
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('logType', {
      id: 'logType',
      header: () => (
        <Text color="gray.400" fontWeight="bold">
          Log Type
        </Text>
      ),
      cell: (info) => (
        <Badge colorScheme={info.getValue() === 'Error' ? 'red' : 'blue'}>
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('logDescription', {
      id: 'logDescription',
      header: () => (
        <Text color="gray.400" fontWeight="bold">
          Description
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('logDate', {
      id: 'logDate',
      header: () => (
        <Text color="gray.400" fontWeight="bold">
          Log Date
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="700">
          {new Date(info.getValue()).toLocaleDateString()}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text color="gray.400" fontWeight="bold">
          Actions
        </Text>
      ),
      cell: (info) => (
        <Flex gap={2}>
          <IconButton icon={<MdVisibility />} aria-label="View" size="sm" />
        </Flex>
      ),
    }),
  ];

  const table = useReactTable({
    data: paginatedLogs,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const paginatedData = logs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <>
      <Box
        pt={{ base: '80px', md: '80px', xl: '80px' }}
        px={{ base: '16px', md: '25px' }}
      >
        <BackButton />
        <Card flexDirection="column" w="100%" px={{ base: '16px', md: '25px' }}>
          <Flex
            px={{ base: '16px', md: '25px' }}
            mb="8px"
            justify="space-between"
            align="center"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 4, md: 0 }}
          >
            <Text
              color={textColor}
              fontSize="24px"
              fontWeight="700"
              lineHeight="100%"
              mb={{ base: '8px', md: '0px' }}
            >
              Clinic Team Logs
            </Text>

            <Box
              w={{ base: '100%', md: 'auto' }}
              display="flex"
              justifyContent={{ base: 'center', md: 'flex-end' }}
            >
              <Pagination
                totalPages={totalPages}
                initialPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </Box>
          </Flex>

          {/* Table */}
          <Box overflowX="auto">
            <Table variant="striped" color="gray.500" mb="24px" mt="12px">
              <Thead>
                {table.getHeaderGroups()?.map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers?.map((header) => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex justifyContent="space-between" align="center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>

              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
        </Card>
      </Box>
    </>
  );
};

export default TeamLogs;
